import { classifyCareLogCategory } from './categoryClassifier';
import {
  classifyAutomaticDurableFact,
  getCareKnowledgePromotionDecision,
  normalizePolicyCategory,
  requiresCareKnowledgeReview,
} from '../features/careSourceProcessing/careKnowledgeDurabilityPolicy';
import {
  getCaptureSuggestionDestination,
} from '../features/careSourceProcessing/captureDestinationPolicy';

const normalizeText = (value = '') => String(value || '').trim();

export const DOCUMENT_DESTINATION_VALUES = Object.freeze({
  TIMELINE: 'timeline',
  GOALS: 'goals',
  PROFILE_OR_KNOWLEDGE: 'profile_or_knowledge',
  STRUCTURED_REVIEW: 'structured_review',
  LIBRARY_ONLY: 'library_only',
  NONE: 'none',
  NEEDS_RESOLUTION: 'needs_resolution',
});

const STRUCTURED_CAPTURE_DESTINATIONS = new Set(['Medications', 'Allergies', 'Health Profile']);

const EVENT_EVIDENCE_PATTERNS = [
  /\b(?:on|by|at|during|after|before)\s+(?:[A-Z][a-z]+\s+)?\d{1,2}(?:st|nd|rd|th)?\b/i,
  /\b(?:developed|transferred|admitted|requested|started|stopped|began|began|moved|fell|broke|called|arrived|left|referred|saw|reported|was taken|was sent|was moved)\b/i,
  /\bfirst time\b/i,
  /\b(?:for the first time|each time|that day|that morning|that afternoon|that evening)\b/i,
];

const PROGRESS_PATTERNS = [
  /\b(?:progress|course|recovery|recovering|improv(?:ed|ing|ement)|better|worse|stable|unchanged|ongoing)\b/i,
  /\b(?:over the last|over the past|this semester|this year|last semester|last year|since admission|since then)\b/i,
  /\b(?:with fluids|with support|after fluids|after treatment|after rest)\b/i,
];

const DATE_REFERENCE_PATTERN = /\b(?:january|february|march|april|may|june|july|august|september|october|november|december|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?|\d{1,2}(?:st|nd|rd|th)?|date|day|month|year)\b/i;

const GUIDANCE_PATTERNS = [
  /\bguidance\b/i,
  /\binstruction(s)?\b/i,
  /\brecommendation(s)?\b/i,
  /\bcare plan\b/i,
  /\bbehavior support plan\b/i,
  /\bsafety plan\b/i,
  /\bfeeding plan\b/i,
  /\baccommodation(s)?\b/i,
  /\bstrategy\b/i,
  /\buse\s+(?:aac|visual schedule|extra time)\b/i,
];

const KNOWLEDGE_PATTERNS = [
  /\ballerg(y|ies|ic)\b/i,
  /\bdiagnos(?:is|ed|es)\b/i,
  /\bmedical history\b/i,
  /\bpast medical history\b/i,
  /\bproblem list\b/i,
  /\bcurrent med(s|ications?)\b/i,
  /\bprescribed\b/i,
  /\bbaseline\b/i,
  /\bcondition\b/i,
  /\bhealth profile\b/i,
  /\bprofile detail\b/i,
  /\bcommunication method\b/i,
];

const STRUCTURED_REVIEW_PATTERNS = [
  /\ballerg(y|ies|ic)\b/i,
  /\bdiagnos(?:is|ed|es)\b/i,
  /\bmedical history\b/i,
  /\bpast medical history\b/i,
  /\bproblem list\b/i,
  /\bcurrent med(s|ications?)\b/i,
  /\bmedication change\b/i,
  /\bprescribed\b/i,
  /\bbaseline\b/i,
  /\bcondition\b/i,
  /\bhealth profile\b/i,
];

const findFirstPattern = (text = '', patterns = []) => patterns.find((pattern) => pattern.test(text));

const hasPattern = (text = '', patterns = []) => Boolean(findFirstPattern(text, patterns));

const normalizeOutcome = (value = '') => normalizeText(value).toLowerCase();

const getCandidateText = (candidate = {}) => normalizeText([
  candidate.sourceText,
  candidate.label,
  candidate.extracted?.sourceText,
  candidate.extracted?.body,
  candidate.extracted?.description,
  candidate.extracted?.title,
].filter(Boolean).join(' '));

const hasSourceContent = (candidate = {}) => Boolean(
  normalizeText(candidate.sourceText)
  || normalizeText(candidate.label)
  || normalizeText(candidate.extracted?.sourceText)
  || normalizeText(candidate.extracted?.body)
  || normalizeText(candidate.extracted?.description)
  || normalizeText(candidate.extracted?.title)
);

const isStructuredSafeguardCandidate = (candidate = {}, probe = {}) => {
  const captureDestination = getCaptureSuggestionDestination({
    knowledgeType: probe.policyCategory,
    factType: probe.policyCategory,
    category: probe.policyCategory,
    title: candidate.label,
    content: probe.text,
    sourceText: probe.text,
  });
  const hasStructuredText = hasPattern(probe.text, STRUCTURED_REVIEW_PATTERNS);

  return STRUCTURED_CAPTURE_DESTINATIONS.has(captureDestination)
    || hasStructuredText;
};

const buildResolutionSignals = (candidate = {}, probe = {}) => {
  const signals = [];
  if (candidate.factType) signals.push(`fact_type:${candidate.factType}`);
  if (probe.policyCategory && probe.policyCategory !== 'other') signals.push(`policy_category:${probe.policyCategory}`);
  if (probe.legacyCategory && probe.legacyCategory !== 'unknown') signals.push(`legacy_category:${probe.legacyCategory}`);
  if (probe.durableFactType) signals.push(`durable_fact:${probe.durableFactType}`);
  if (probe.reviewsRequired) signals.push('care_knowledge_review_required');
  if (probe.captureDestination) signals.push(`capture_destination:${probe.captureDestination}`);
  return signals;
};

const resolveTimelineForEvent = (candidate = {}, probe = {}) => {
  const eventEvidence = hasPattern(probe.text, EVENT_EVIDENCE_PATTERNS);
  if (candidate.factType === 'event' && eventEvidence) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.TIMELINE,
      confidence: 'high',
      reason: 'Concrete event evidence supports Timeline routing.',
      signals: ['fact_type:event', 'event_evidence'],
    };
  }

  if (candidate.factType === 'history_progress' && eventEvidence) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.TIMELINE,
      confidence: 'medium',
      reason: 'History/progress text contains concrete event evidence.',
      signals: ['fact_type:history_progress', 'event_evidence'],
    };
  }

  return null;
};

const resolveHistoryProgress = (candidate = {}, probe = {}) => {
  const eventResolution = resolveTimelineForEvent(candidate, {
    ...probe,
    text: probe.sourceTextOnly || probe.text,
  });
  if (eventResolution) return eventResolution;

  const signalText = probe.sourceTextOnly || probe.text;
  const hasProgressLanguage = hasPattern(signalText, PROGRESS_PATTERNS);
  const hasKnowledgeLanguage = hasPattern(signalText, KNOWLEDGE_PATTERNS) || hasPattern(signalText, GUIDANCE_PATTERNS);
  const hasLegacySignal = probe.legacyCategory && probe.legacyCategory !== 'unknown';

  if (hasProgressLanguage && !hasKnowledgeLanguage) {
    if (signalText && DATE_REFERENCE_PATTERN.test(signalText) && !hasPattern(signalText, EVENT_EVIDENCE_PATTERNS)) {
      return {
        destination: DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION,
        confidence: 'low',
        reason: 'History/progress text includes time markers but no concrete event evidence.',
        signals: ['fact_type:history_progress', 'time_marker_only'],
      };
    }

    return {
      destination: DOCUMENT_DESTINATION_VALUES.LIBRARY_ONLY,
      confidence: 'medium',
      reason: 'History/progress text reads like narrative progress rather than a distinct event.',
      signals: ['fact_type:history_progress', 'progress_language'],
    };
  }

  if (hasKnowledgeLanguage) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.PROFILE_OR_KNOWLEDGE,
      confidence: 'medium',
      reason: 'History/progress text looks like reusable knowledge or guidance.',
      signals: ['fact_type:history_progress', 'knowledge_language'],
    };
  }

  if (signalText && DATE_REFERENCE_PATTERN.test(signalText)) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION,
      confidence: 'low',
      reason: 'History/progress text includes time markers but no concrete event evidence.',
      signals: ['fact_type:history_progress', 'time_marker_only'],
    };
  }

  return {
    destination: DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION,
    confidence: 'low',
    reason: 'History/progress text does not clearly separate event, progress, or guidance.',
    signals: ['fact_type:history_progress', 'insufficient_evidence'],
  };
};

const resolveReferenceGuidance = (candidate = {}, probe = {}) => {
  const eventResolution = resolveTimelineForEvent(candidate, probe);
  if (eventResolution) {
    return {
      ...eventResolution,
      reason: 'Reference/guidance text includes a separate concrete event.',
    };
  }

  const hasGuidanceLanguage = hasPattern(probe.text, GUIDANCE_PATTERNS);
  const hasKnowledgeLanguage = hasPattern(probe.text, KNOWLEDGE_PATTERNS);
  const hasLegacySignal = probe.legacyCategory && probe.legacyCategory !== 'unknown';
  const captureDestination = probe.captureDestination || '';

  if (hasGuidanceLanguage || hasKnowledgeLanguage || captureDestination === 'Goals') {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.PROFILE_OR_KNOWLEDGE,
      confidence: 'medium',
      reason: 'Reference/guidance text is reusable knowledge rather than a destination event.',
      signals: ['fact_type:reference_guidance', 'guidance_or_knowledge_language'],
    };
  }

  if (hasLegacySignal || normalizeOutcome(candidate.documentPurpose?.type) === 'plan_guidance_care_reference') {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.LIBRARY_ONLY,
      confidence: 'medium',
      reason: 'Reference/guidance text is archival without a stronger reusable-knowledge signal.',
      signals: ['fact_type:reference_guidance', 'archival_reference'],
    };
  }

  return {
    destination: DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION,
    confidence: 'low',
    reason: 'Reference/guidance text is not specific enough for a destination.',
    signals: ['fact_type:reference_guidance', 'insufficient_evidence'],
  };
};

const resolveStandingFact = (candidate = {}, probe = {}) => {
  if (probe.structuredReview) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.STRUCTURED_REVIEW,
      confidence: 'high',
      reason: 'Standing fact hits existing structured-review/current-state safeguards.',
      signals: ['fact_type:standing_fact', 'structured_review_guard'],
    };
  }

  return {
    destination: DOCUMENT_DESTINATION_VALUES.PROFILE_OR_KNOWLEDGE,
    confidence: 'medium',
    reason: 'Standing fact is reusable profile/knowledge content.',
    signals: ['fact_type:standing_fact'],
  };
};

const resolveGoal = (candidate = {}, probe = {}) => {
  if (probe.structuredReview) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.STRUCTURED_REVIEW,
      confidence: 'high',
      reason: 'Goal text crosses structured review safeguards.',
      signals: ['fact_type:goal', 'structured_review_guard'],
    };
  }

  return {
    destination: DOCUMENT_DESTINATION_VALUES.GOALS,
    confidence: 'medium',
    reason: 'Goal fact maps to existing goals path.',
    signals: ['fact_type:goal'],
  };
};

const resolveEvent = (candidate = {}, probe = {}) => {
  const eventResolution = resolveTimelineForEvent(candidate, probe);
  if (eventResolution) return eventResolution;

  if (probe.text) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION,
      confidence: 'low',
      reason: 'Event fact lacks adequate concrete event evidence.',
      signals: ['fact_type:event', 'insufficient_event_evidence'],
    };
  }

  return {
    destination: DOCUMENT_DESTINATION_VALUES.NONE,
    confidence: 'low',
    reason: 'Empty event candidate carries no destination value.',
    signals: ['fact_type:event', 'empty_candidate'],
  };
};

const resolveAmbiguous = (candidate = {}) => ({
  destination: candidate.sourceText || candidate.label ? DOCUMENT_DESTINATION_VALUES.NEEDS_RESOLUTION : DOCUMENT_DESTINATION_VALUES.NONE,
  confidence: 'low',
  reason: candidate.sourceText || candidate.label
    ? 'Candidate remains ambiguous after fact typing.'
    : 'Empty ambiguous candidate carries no destination value.',
  signals: [candidate.sourceText || candidate.label ? 'fact_type:ambiguous' : 'empty_candidate'],
});

const resolveByFactType = (candidate = {}, probe = {}) => {
  if (!hasSourceContent(candidate)) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.NONE,
      confidence: 'low',
      reason: 'Candidate has no extracted content to route.',
      signals: ['empty_candidate'],
    };
  }

  if (probe.structuredReview) {
    return {
      destination: DOCUMENT_DESTINATION_VALUES.STRUCTURED_REVIEW,
      confidence: 'high',
      reason: 'Existing structured-review safeguard applies.',
      signals: ['structured_review_guard'],
    };
  }

  switch (candidate.factType) {
    case 'event':
      return resolveEvent(candidate, probe);
    case 'standing_fact':
      return resolveStandingFact(candidate, probe);
    case 'goal':
      return resolveGoal(candidate, probe);
    case 'history_progress':
      return resolveHistoryProgress(candidate, probe);
    case 'reference_guidance':
      return resolveReferenceGuidance(candidate, probe);
    case 'ambiguous':
    default:
      return resolveAmbiguous(candidate);
  }
};

const buildProbe = (candidate = {}) => {
  const text = getCandidateText(candidate);
  const sourceTextOnly = normalizeText(candidate.sourceText || candidate.extracted?.sourceText || candidate.extracted?.body || candidate.extracted?.description || candidate.extracted?.title);
  const policyProbe = {
    title: candidate.label || '',
    content: text,
    summary: text,
    sourceText: text,
    factType: candidate.factType || '',
    knowledgeType: candidate.factType || '',
    sourceType: 'file_import',
    sourceContext: 'imported_document',
    sourceProvenance: { origin: 'imported_document' },
  };
  const policyCategory = normalizePolicyCategory(policyProbe);
  const legacyCategory = classifyCareLogCategory(text);
  const durableFactType = classifyAutomaticDurableFact(policyProbe);
  const promotionDecision = getCareKnowledgePromotionDecision(policyProbe);
  const captureDestination = getCaptureSuggestionDestination({
    knowledgeType: policyCategory,
    factType: policyCategory,
    category: policyCategory,
    title: candidate.label,
    content: text,
    sourceText: text,
  });
  const reviewsRequired = requiresCareKnowledgeReview(policyProbe);
  const structuredReview = isStructuredSafeguardCandidate(candidate, {
    text,
    policyCategory,
    legacyCategory,
    durableFactType,
    promotionDecision,
    captureDestination,
    reviewsRequired,
  })
    || captureDestination === 'Medications'
    || captureDestination === 'Allergies'
    || captureDestination === 'Health Profile';

  return {
    text,
    sourceTextOnly,
    policyCategory,
    legacyCategory,
    durableFactType,
    promotionDecision,
    captureDestination,
    reviewsRequired,
    structuredReview,
  };
};

export const resolveDocumentFactDestination = (candidate = {}) => {
  const probe = buildProbe(candidate);
  const baseResolution = resolveByFactType(candidate, probe);
  const resolutionSignals = [
    ...buildResolutionSignals(candidate, probe),
    ...(baseResolution.signals || []),
  ];

  return {
    schemaVersion: 1,
    key: candidate.key ? `${candidate.key}::${baseResolution.destination}` : '',
    sourceFactCandidateKey: candidate.key || '',
    origin: candidate.origin,
    factType: candidate.factType,
    destination: baseResolution.destination,
    destinationConfidence: baseResolution.confidence,
    resolutionReason: baseResolution.reason,
    resolutionSignals,
    documentPurpose: candidate.documentPurpose || candidate.origin?.metadata?.documentPurpose || null,
    sourceText: candidate.sourceText || '',
    extracted: candidate.extracted || {},
    provenance: candidate.provenance || candidate.findingMetadata || {},
    findingMetadata: candidate.findingMetadata || {},
  };
};

export const buildDocumentDestinationCandidatesFromFactCandidates = (candidates = []) => (
  Array.isArray(candidates)
    ? candidates.map((candidate) => resolveDocumentFactDestination(candidate)).filter(Boolean)
    : []
);

export default buildDocumentDestinationCandidatesFromFactCandidates;
